import React from 'react';
import { useTranslation } from 'react-i18next';
import PageSEO from '../components/PageSEO';
import Card from '../components/card/Card';
import Table from '../components/table/Table';
import Accordion from '../components/accordion/Accordion';
import ProjectProcess from '../components/projectProcess/ProjectProcess';
import ImgPayment from '../img/pricePage/OnlinePaymentSolutions.webp';
import ImgChoice from '../img/pricePage/PricingPackages.webp';
import InfoSection from '../components/infoSection/InfoSection';
import FaqAccordion from '../components/accordion/FaqAccordion';

const PricePage = () => {
  const { t } = useTranslation('prices');
  const { t: tPrice } = useTranslation('websitePrice');
  const websitePrices = tPrice('websitePrices', { returnObjects: true });
  const carePlanCards = tPrice('carePlanCards', { returnObjects: true });

  return (
    <main>
      <PageSEO
        title={`${t('title')} ${t('titleAccent')} — ${t('carePlans.title')} ${t('carePlans.titleAccent')} | PixelCraft`}
        description={t('infoSection1.p1')}
        path="/prices"
      />
      {/* WEBSITE PACKAGES */}
      <section className="price section" id="webPrice">
        <div className="container">
          <h2 className="title-2">
            {t('title')} <span className="accent">{t('titleAccent')}</span>
          </h2>
          <InfoSection
            paragraphs={[t('infoSection1.p1'), t('infoSection1.p2')]}
            buttonText={t('infoSection1.button')}
            buttonLink="/contacts"
            imageSrc={ImgPayment}
            imageAlt="Payment stages"
          />
          <div className="price__cards">
            <div className="prices__cards-inner">
              {websitePrices.map((item, index) => (
                <Card
                  key={index}
                  card_title={item.title}
                  descriptions={item.descriptions}
                  price={item.price}
                  cta={item.cta}
                  link={item.link}
                  dropdownItems={item.subpackages || undefined}
                />
              ))}
            </div>
            <div className="price__cards-descr">
              <p>{t('vatNote')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CARE PLANS */}
      <section className="care-plans section" id="carePlans">
        <div className="container">
          <h2 className="title-2">
            {t('carePlans.title')} <span className="accent">{t('carePlans.titleAccent')}</span>
          </h2>
          <p className="faq-section__subtitle">{t('carePlans.subtitle')}</p>
          <div className="price__cards">
            <div className="prices__cards-inner">
              {carePlanCards.map((item, index) => (
                <Card key={index} card_title={item.title} descriptions={item.descriptions} price={item.price} cta={item.cta} link={item.link} />
              ))}
            </div>
            <div className="price__cards-descr">
              <p>{t('carePlans.vatNote')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PAYMENT TERMS */}
      <section className="payment-terms section" id="paymentTerms">
        <div className="container">
          <h2 className="title-2">
            {t('paymentTerms.title')} <span className="accent">{t('paymentTerms.titleAccent')}</span>
          </h2>
          <p className="faq-section__subtitle">{t('paymentTerms.subtitle')}</p>
          <div className="payment-terms__grid">

            {/* Option 1 — Standard */}
            <div className="payment-card">
              <span className="payment-card__tag">{t('paymentTerms.option1.tag')}</span>
              <h3 className="payment-card__title">{t('paymentTerms.option1.title')}</h3>
              <div className="payment-card__steps">
                <div className="payment-step">
                  <div className="payment-step__number">1</div>
                  <div className="payment-step__content">
                    <strong>{t('paymentTerms.option1.step1Title')}</strong>
                    <p>{t('paymentTerms.option1.step1Desc')}</p>
                  </div>
                </div>
                <div className="payment-step">
                  <div className="payment-step__number">2</div>
                  <div className="payment-step__content">
                    <strong>{t('paymentTerms.option1.step2Title')}</strong>
                    <p>{t('paymentTerms.option1.step2Desc')}</p>
                  </div>
                </div>
              </div>
              <p className="payment-card__note">{t('paymentTerms.option1.note')}</p>
            </div>

            {/* Option 2 — Installment */}
            <div className="payment-card payment-card--accent">
              <span className="payment-card__tag payment-card__tag--accent">{t('paymentTerms.option2.tag')}</span>
              <h3 className="payment-card__title">{t('paymentTerms.option2.title')}</h3>
              <div className="payment-card__steps">
                <div className="payment-step">
                  <div className="payment-step__number">1</div>
                  <div className="payment-step__content">
                    <strong>{t('paymentTerms.option2.step1Title')}</strong>
                    <p>{t('paymentTerms.option2.step1Desc')}</p>
                  </div>
                </div>
                <div className="payment-step">
                  <div className="payment-step__number">2</div>
                  <div className="payment-step__content">
                    <strong>{t('paymentTerms.option2.step2Title')}</strong>
                    <p>{t('paymentTerms.option2.step2Desc')}</p>
                  </div>
                </div>
              </div>
              <p className="payment-card__note">{t('paymentTerms.option2.note')}</p>
              <a href="/contacts" className="button">{t('paymentTerms.option2.cta')}</a>
            </div>

          </div>
        </div>
      </section>

      {/* PROJECT PROCESS */}
      <ProjectProcess />

      {/* HOW TO CHOOSE */}
      <section className="choice section" id="choice">
        <div className="container">
          <h2 className="title-2">
            {t('choiceSection.title')} <span className="accent">{t('choiceSection.titleAccent')}</span> {t('choiceSection.titleEnd')}
          </h2>
          <InfoSection
            paragraphs={[t('choiceSection.p1'), t('choiceSection.p2'), t('choiceSection.p3')]}
            buttonText={t('choiceSection.button')}
            buttonLink="/contacts"
            imageSrc={ImgChoice}
            imageAlt="Comparison table"
          />
          <Table />
          <Accordion />

          <section className="section faq-section" id="FAQ">
            <div className="container">
              <h2 className="title-2">
                {t('faq.title')} <span className="accent">{t('faq.titleAccent')}</span>
              </h2>
              <h3 className="faq-section__subtitle">{t('faq.subtitle')}</h3>
              <FaqAccordion />
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default PricePage;
